import { Alert, Button, Flex, Form, Space } from 'antd';
import { FC } from 'react';

import { ButtonSuccess, CodeEditor, DescriptionBlock } from '@/common/components';
import { CodeEditorLanguage } from '@/common/components/CodeEditor/CodeEditor.interface';
import { AppFormProps, BaseQueryParams } from '@/common/interfaces';
import { getDateFactory } from '@/common/utils/date';
import { defineInputStatus } from '@/common/utils/form';

import {
  PagesCriticalCssFormFields,
  PagesCriticalCssGenerationStatus,
  PagesCriticalCssInputs,
  useGetPageCriticalCssQuery,
} from '@/modules/Pages';

interface Props extends AppFormProps<typeof PagesCriticalCssInputs, PagesCriticalCssFormFields> {
  pageCriticalCss: BaseQueryParams<typeof useGetPageCriticalCssQuery>['data'];
  isPageCriticalCssLoading: boolean;
  handleGenerate: () => void;
}

export const PagesCriticalCssFormView: FC<Props> = ({
  pageCriticalCss,
  isPageCriticalCssLoading,
  handleGenerate,
  handleFinish,
  handleFinishFailed,
  handleBack,
  form,
  formName,
  formErrors,
  isFormLoading,
  initialValues,
  validationSchema,
}) => {
  const isActionDisabled = isFormLoading || (pageCriticalCss.generationStatus === PagesCriticalCssGenerationStatus.Queued || pageCriticalCss.generationStatus === PagesCriticalCssGenerationStatus.Generating);

  return (
    <Form
      form={form}
      name={formName}
      initialValues={initialValues}
      autoComplete="off"
      onFinish={handleFinish}
      onFinishFailed={handleFinishFailed}
      layout="vertical"
      disabled={isFormLoading}
    >
      {pageCriticalCss.generationStatus === PagesCriticalCssGenerationStatus.GenerationFailed && (
        <Alert
          type="error"
          showIcon
          message="Помилка генерації Critical CSS"
          description={pageCriticalCss.generationError ?? 'Невідома помилка'}
          style={{ marginBottom: 16, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        />
      )}

      <Form.Item
        name={PagesCriticalCssInputs.Css}
        hasFeedback
        help={formErrors?.css}
        validateStatus={defineInputStatus(isFormLoading, formErrors?.css)}
        rules={validationSchema.css}
        style={{ marginBottom: 16 }}
      >
        <CodeEditor
          language={CodeEditorLanguage.Css}
          onChange={(value) => form.setFieldValue(PagesCriticalCssInputs.Css, value ?? '')}
          disabled={isActionDisabled}
          loading={isPageCriticalCssLoading}
        />
      </Form.Item>

      <Form.Item noStyle>
        <DescriptionBlock items={[['Дата оновлення', pageCriticalCss.updatedAt ? getDateFactory(pageCriticalCss.updatedAt, 'Thursday, August 16, 2018 08:02', true) : '—']]} />
      </Form.Item>

      <Form.Item wrapperCol={{ span: 24 }} style={{ marginBottom: 0 }}>
        <Flex justify="space-between" align="center">
          <Button type="primary" loading={isFormLoading} onClick={handleGenerate} disabled={isActionDisabled}>
            Згенерувати
          </Button>

          <Space>
            <Button type="default" onClick={handleBack} disabled={isFormLoading}>
              Скасувати
            </Button>
            <ButtonSuccess loading={isFormLoading} type="primary" htmlType="submit" disabled={isActionDisabled}>
              Зберегти
            </ButtonSuccess>
          </Space>
        </Flex>
      </Form.Item>
    </Form>
  );
};
